import { HttpResult } from "widgets-for-react"
import { ApiException, ErrorResponse } from "./generated"

export declare type ExtendedHttpResult<a> = HttpResult<a> | ({
  kind: "failedWithMessage"
  message: string
} & {
  status: number
})

export const isErrorResponse = (object: any): object is ErrorResponse => {
  return object && object['Status'] === 'Error'
}

export const httpResult = async <T>(promise: () => Promise<T>): Promise<HttpResult<T>> => {
  try {
    return {
      kind: 'result',
      value: await promise(),
      status: 200
    }
  } catch (error) {
    if (isErrorResponse(error)) return { kind: 'failed', status: error.HttpStatusCode }
    if (ApiException.isApiException(error)) return { kind: 'failed', status: error.status }
    console.error(error)
    return {
      kind: 'failed',
      status: 500
    }
  }
}

export const extendedHttpResult = async <T>(promise: () => Promise<T>): Promise<ExtendedHttpResult<T>> => {
  try {
    return {
      kind: 'result',
      value: await promise(),
      status: 200
    }
  } catch (error) {
    if (isErrorResponse(error)) return {
      kind: 'failedWithMessage',
      message: error.Message,
      status: error.HttpStatusCode
    }
    if (ApiException.isApiException(error)) return {
      kind: 'failedWithMessage',
      message: error.message,
      status: error.status
    }
    console.error(error)
    return {
      kind: 'failed',
      status: 500
    }
  }
}

export const addParamsToUrl = <T extends Record<string, any>>(params: T, addHistory: boolean = false): void => {
  const url = new URL(window.location.href)

  // Clear existing parameters
  url.search = ''; // it reset the search params

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.delete(key)
    if (Array.isArray(value)) {
      value.forEach(item => url.searchParams.append(key, String(item)))
    } else if (value instanceof Date) {
      url.searchParams.set(key, value.toISOString())
    } else if (value) {
      url.searchParams.set(key, String(value))
    }
  })

  addHistory
    ? window.history.pushState({}, '', url.toString())
    : window.history.replaceState({}, '', url.toString())
}

export const parseParamsFromUrl = <T extends Record<string, any>>(defaults: T): T => {
  const url = new URL(window.location.href)
  const params = new URLSearchParams(url.search)

  const result: T = { ...defaults }

  Object.entries(defaults).forEach(([key, defaultValue]) => {
    const value = params.get(key)

    if (value !== null) {
      if (defaultValue instanceof Date) {
        // Parse dates
        const date = new Date(value)
        result[key as keyof T] = (isNaN(date.getTime()) ? defaultValue : date) as T[keyof T]
      } else if (Array.isArray(defaultValue)) {
        // Parse arrays (get all values for the key)
        result[key as keyof T] = params.getAll(key) as T[keyof T]
      } else if (typeof defaultValue === 'number') {
        // Parse numbers
        result[key as keyof T] = (isNaN(Number(value)) ? defaultValue : Number(value)) as T[keyof T]
      } else if (typeof defaultValue === 'boolean') {
        // Parse booleans
        result[key as keyof T] = (value === 'true') as T[keyof T]
      } else {
        // Assume string or other types
        result[key as keyof T] = value as T[keyof T]
      }
    } else {
      // Use the default value if no query parameter exists
      result[key as keyof T] = defaultValue
    }
  })

  return result as T
}

// Gratefully stolen from https://dev.to/dvddpl/revive-your-json-examples-with-tdd-and-composition-3#add-even-more-flexibility-with-partial-application
type ReviverFunc = (key: string, value: any) => any
export const pipeRevivers = (revivers: ReviverFunc[]) => (key: string, value: any) => revivers.reduce((v, f) => f(key, v), value);

export const upperCase = (prop: string) => (key: string, value: any) =>
  key === prop ? value.toUpperCase() : value

export const parseDate = (prop: string) => (key: string, value: any) =>
  key === prop ? new Date(Date.parse(value)) : value
